/* Reading the futures account, and showing what it would change before it does.
 *
 * Nothing here writes on the way in. The exchange is asked, the answer is lined
 * up against the book, and the difference is put on screen with both numbers
 * side by side — then the write is a second, deliberate press. That order is
 * not politeness: the numbers coming back are the ones the whole leverage
 * screen is built on, and a pull that silently replaced them would be a way to
 * lose a hand-corrected figure without ever seeing it go.
 *
 * What applying does, exactly, and what it does not:
 *
 *   - It corrects the contracts and the free balance. Both are records of a
 *     state the exchange already holds.
 *   - It moves no cash. Opening a contract in this book debits the wallet,
 *     because in the book that is when the margin leaves. Here the margin left
 *     days ago and MEXC's free balance already has it out — replaying it as an
 *     opening would post the same margin twice.
 *   - It closes nothing and realises nothing. A contract the exchange no longer
 *     reports is listed and left, because what it came to is the fill, and the
 *     fill is not in this answer. */

import { useState } from 'react';
import { BrokerLinkUnavailable, describe } from '../lib/brokerLink';
import { dateTime, moneyExact } from '../lib/format';
import { MexcRefused, fetchAssets, fetchOpenPositions, whatToCheck } from '../lib/mexc';
import { isUnchanged, reconcile, type MexcSnapshot } from '../state/mexcAccount';
import { useStore } from '../state/store';
import type { PanelProps } from './BrokerConnection';

function Line({ k, v, warn }: { k: string; v: string; warn?: boolean }) {
  return (
    <div className="kv">
      <span className="dim">{k}</span>
      <span style={{ marginLeft: 'auto', fontWeight: 600 }} className={warn ? 'warn' : undefined}>{v}</span>
    </div>
  );
}


export function MexcPanel({ values, account, say, blocked }: PanelProps) {
  const { state, setCash, putPerp, saveSettings } = useStore();
  const auto = state.settings.mexcAuto !== false;
  const [busy, setBusy] = useState(false);
  const [snap, setSnap] = useState<MexcSnapshot | null>(null);

  const held = state.data.cash.find((c) => c.account === account);
  const accountRecord = state.data.accounts.find((a) => a.id === account);
  const currency = accountRecord?.currency ?? 'USD';

  const read = async () => {
    setBusy(true);
    setSnap(null);
    try {
      const keys = { apiKey: values['apiKey']!.trim(), secret: values['secret']!.trim() };
      /* Two calls, one after the other rather than at once: a key that has not
         been given futures reading fails the first, and there is no reason to
         make the second one to learn the same thing twice. */
      const assets = await fetchAssets(keys);
      const positions = await fetchOpenPositions(keys);
      const result = reconcile(assets, positions, state.data.perps, new Date().toISOString());
      setSnap(result);
      if (result.wallet === null && result.changes.length === 0) {
        say('MEXC answered, but with no USDT balance and no open contracts on this key.');
      }
    } catch (e) {
      if (e instanceof BrokerLinkUnavailable) say(e.message);
      else if (e instanceof MexcRefused) {
        say(`MEXC refused it (${e.code}): ${e.message} ${whatToCheck(e.code)}`);
      } else say(`Could not read the account: ${describe(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!snap) return;
    setBusy(true);
    try {
      const writing = snap.changes.filter((c) => !isUnchanged(c));
      for (const change of writing) await putPerp(change.perp);

      let cashMoved = false;
      if (snap.wallet && Math.abs((held?.amount ?? 0) - snap.wallet.free) >= 0.0001) {
        await setCash(account, snap.wallet.free, currency);
        cashMoved = true;
      }

      const parts: string[] = [];
      if (writing.length) parts.push(`${writing.length} contract(s) corrected`);
      if (cashMoved) parts.push('free balance set');
      say(parts.length ? `${parts.join(', ')}. No cash was moved and nothing was closed.` : 'Nothing needed changing.');
      setSnap(null);
    } catch (e) {
      say(`Could not write it: ${describe(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const changing = snap ? snap.changes.filter((c) => !isUnchanged(c)).length : 0;
  const walletMoves = snap?.wallet
    ? Math.abs((held?.amount ?? 0) - snap.wallet.free) >= 0.0001
    : false;

  return (
    <>
      <div className="fab-row" style={{ marginTop: 0 }}>
        <button className="btn btn-primary btn-block" disabled={busy || blocked !== null}
                onClick={() => void read()}>
          {busy && !snap ? 'Reading the account…' : 'Read my MEXC account'}
        </button>
      </div>

      {snap ? (
        <div className="card card-pad" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>On MEXC now</div>
          <div className="hint" style={{ marginTop: 2, marginBottom: 12 }}>
            Compared with this book. The app reads this on its own every few minutes while it is
            open, so it usually matches already.
          </div>

          {snap.wallet ? (
            <div className="kv-list" style={{ marginBottom: 14 }}>
              <Line
                k="Free to use"
                v={walletMoves
                  ? `${snap.wallet.free.toFixed(2)} USDT (book: ${(held?.amount ?? 0).toFixed(2)})`
                  : `${snap.wallet.free.toFixed(2)} USDT`}
                warn={walletMoves}
              />
              <Line k="Posted as margin" v={`${snap.wallet.margin.toFixed(2)} USDT`} />
              <Line k="Open profit or loss"
                    v={`${snap.wallet.unrealized >= 0 ? '+' : ''}${snap.wallet.unrealized.toFixed(2)} USDT`} />
              <Line k="Futures wallet in total" v={`${snap.wallet.equity.toFixed(2)} USDT`} />
            </div>
          ) : (
            <div className="hint" style={{ marginBottom: 14 }}>
              MEXC sent no USDT balance, so the free balance here is left as it is.
            </div>
          )}

          {snap.changes.length === 0 ? (
            <div className="empty">No contracts are open at MEXC.</div>
          ) : (
            <div className="rows">
              {snap.changes.map((c) => {
                const b = c.before;
                const same = isUnchanged(c);
                return (
                  <div className="row" key={c.perp.symbol}>
                    <div className="row-top">
                      <span className="row-ticker">{c.perp.display}</span>
                      <span className="tag">{c.perp.side} {c.perp.leverage}x</span>
                      <span className="tag" data-kind={b !== null && same ? 'ok' : 'warn'}>
                        {b === null ? 'new' : same ? 'matches the book' : 'will be updated'}
                      </span>
                    </div>
                    <div className="kv-list" style={{ marginTop: 8 }}>
                      <Figure k="Margin" before={b?.marginUsdt ?? null} after={c.perp.marginUsdt} unit=" USDT" />
                      <Figure k="Size at entry" before={b?.sizeUsdt ?? null} after={c.perp.sizeUsdt} unit=" USDT" />
                      <Figure k="Entry" before={b?.entryPrice ?? null} after={c.perp.entryPrice} />
                      <Figure k="Liquidation" before={b?.liqPrice ?? null} after={c.perp.liqPrice} />
                    </div>
                    {c.notes.map((n) => (
                      <div className="hint" key={n} style={{ marginTop: 6 }}>{n}</div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {snap.notAtExchange.length > 0 ? (
            <div className="warn" style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.45 }}>
              {snap.notAtExchange.map((p) => p.display).join(', ')}{' '}
              {snap.notAtExchange.length === 1 ? 'is' : 'are'} open in this book but not on MEXC.
              Nothing is closed automatically: close {snap.notAtExchange.length === 1 ? 'it' : 'them'}{' '}
              on the contract with the result MEXC shows.
            </div>
          ) : null}

          {snap.skipped.length > 0 ? (
            <div className="hint" style={{ marginTop: 10 }}>
              {snap.skipped.map((x) => `${x.symbol}: ${x.why}`).join(' · ')}
            </div>
          ) : null}

          <div className="hint" style={{ marginTop: 14, lineHeight: 1.45 }}>
            Updating copies MEXC&rsquo;s figures into the book. It moves no money, closes nothing and
            realises nothing. USDT is kept as {currency}, one for one.
          </div>

          <div className="fab-row" style={{ marginTop: 12 }}>
            <button className="btn btn-block" onClick={() => setSnap(null)}>Close</button>
            <button className="btn btn-primary btn-block" disabled={busy || (changing === 0 && !walletMoves)}
                    onClick={() => void apply()}>
              {changing === 0 && !walletMoves
                ? 'Nothing to update'
                : `Update${changing ? ` ${changing} contract${changing === 1 ? '' : 's'}` : ''}${walletMoves ? `${changing ? ' and' : ''} the balance` : ''}`}
            </button>
          </div>
        </div>
      ) : null}

      {snap === null ? (
        <>
          {/* Reading it on its own is the normal state; the button above is for
              the first time a key is used, and for when you want to see every
              difference before it lands. */}
          <button
            className="btn btn-sm btn-block" style={{ marginTop: 10 }}
            aria-pressed={auto}
            onClick={() => void saveSettings({ mexcAuto: !auto })}
          >
            {auto ? '✓ Reading it on its own' : 'Read it on its own'}
          </button>
          <div className="hint">
            Every few minutes while the app is open, and never while it is not. It corrects the
            balance and the open contracts, the things MEXC is simply the authority on, and still
            closes nothing: a contract it stops reporting is left for you, because what it came to
            is the fill you got.
            {state.settings.mexcSyncedAt
              ? ` Last read ${dateTime(state.settings.mexcSyncedAt)}.`
              : ''}
          </div>
          {state.settings.mexcError ? (
            <div className="hint hint-warn" style={{ marginTop: 6 }}>
              Last attempt failed: {state.settings.mexcError}
            </div>
          ) : null}
          {held ? (
            <div className="hint" style={{ marginTop: 10 }}>
              The book currently holds {moneyExact(held.amount, currency as 'USD')} free in this account.
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/** One of MEXC's figures for a contract, with what the book had beside it when
    the two differ. Shown only then: "was 922.36" under every line that has not
    moved reads as a change that did not happen. */
function Figure({ k, before, after, unit = '' }: {
  k: string; before: number | null; after: number; unit?: string;
}) {
  const differs = before !== null && Math.abs(before - after) >= 0.005;
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className="v num">
        {after.toFixed(2)}{unit}
        {differs ? <span className="dim" style={{ fontSize: 11, marginLeft: 6 }}>was {before!.toFixed(2)}</span> : null}
      </span>
    </div>
  );
}
